import accounting from 'accounting'
import Decimal from 'decimal.js-light'
import moment from 'moment'
import * as R from 'ramda'
import { issuerCountries } from './constants'

// Accounting.js global settings
accounting.settings.currency.format = '%s %v'
accounting.settings.currency.decimal = '.'
accounting.settings.currency.thousand = ','

// Helpers
const validateAndCastToDecimal = (val) => {
  if (val !== null) {
    if (!(val instanceof Decimal)) val = new Decimal(val)
    return val
  }
  return
}

const validateAndCoercePrecision = (precision) => {
  if (!(precision instanceof Decimal)) precision = new Decimal(precision)
  if (
    !(precision.isInteger() && (precision.isPositive() || precision.isZero()))
  ) {
    throw new Error('Precision should be an integer greater or equal to 0')
  }
  return precision.toNumber()
}

const validateAndCastMomentDate = (date, utc = true) => {
  date = utc ? moment.utc(date) : moment(date)
  if (!date.isValid()) {
    throw new Error('Invalid date passed as argument')
  }
  return date
}

const CURRENCY_ISO_CODE_SYMBOL_MAP = {
  EUR: '\u20AC',
  GBP: '\u00A3'
}

const formatCurrency = (val, currencyIsoCode, precision = 2) => {
  const symbol =
    CURRENCY_ISO_CODE_SYMBOL_MAP[currencyIsoCode] || currencyIsoCode
  return accounting.formatMoney(val, symbol, precision)
}

const formatNumber = (val, precision = 2) => {
  return accounting.formatNumber(val, precision)
}

const daysToDate = (endDate, startDate) => {
  endDate = validateAndCastMomentDate(endDate)
  startDate = startDate ? validateAndCastMomentDate(startDate) : moment.utc()
  return endDate.startOf('day').diff(startDate.startOf('day'), 'days')
}

const rounding = (val, precision = 2, roundingMode = Decimal.ROUND_HALF_UP) => {
  val = validateAndCastToDecimal(val)
  precision = validateAndCoercePrecision(precision)
  return val.toDecimalPlaces(precision, roundingMode).toNumber()
}

// Utility functions
export const bankersRounding = (amount, precision = 0) => {
  amount = validateAndCastToDecimal(amount)
  precision = validateAndCoercePrecision(precision)
  return amount.toDecimalPlaces(precision, Decimal.ROUND_HALF_EVEN).toNumber()
}

export const toCents = (val, precision = 2) => {
  val = new Decimal(bankersRounding(val, precision))
  return val
    ? val
        .toDecimalPlaces(precision)
        .times(new Decimal(10).pow(precision))
        .toNumber()
    : 0
}

export const fromCents = (val = 0) => {
  val = validateAndCastToDecimal(val)
  return val ? val.div(new Decimal(10).pow(2)).toNumber() : 0
}

export const toPercentage = (val, precision = Decimal.precision) => {
  val = validateAndCastToDecimal(val)
  precision = validateAndCoercePrecision(precision)
  return val.times(100).toDecimalPlaces(precision).toNumber()
}

export const formatToPercentage = (val, precision = Decimal.precision) => {
  return `${toPercentage(val, precision)}%`
}

export const fromPercentage = (val, precision = Decimal.precision) => {
  val = validateAndCastToDecimal(val)
  precision = validateAndCoercePrecision(precision)
  return val.div(new Decimal(10).pow(precision)).toNumber()
}

export const formatAmountForDisplay = (val, currencyIsoCode, precision = 2) => {
  return currencyIsoCode
    ? formatCurrency(fromCents(val), currencyIsoCode, precision)
    : formatNumber(fromCents(val), precision)
}

export const calcDiscount = (size, amount) => {
  size = validateAndCastToDecimal(size)
  amount = validateAndCastToDecimal(amount)
  return Decimal(1).minus(amount.dividedBy(size)).toNumber()
}

export const calcAnnualizedYield = (size, amount, startDate, endDate) => {
  const investmentYield = validateAndCastToDecimal(calcYield(size, amount))
  const interval = daysToDate(endDate, startDate)
  return investmentYield.div(interval).times(365).toNumber()
}

export function calcYield(size, amount) {
  size = validateAndCastToDecimal(size)
  amount = validateAndCastToDecimal(amount)

  if (amount.isZero()) {
    return 0
  }

  return size.div(amount).minus(1).toNumber()
}

export const floor = (val, precision) => {
  return rounding(val, precision, Decimal.ROUND_FLOOR)
}

export const round = (val, precision) => {
  return rounding(val, precision, Decimal.ROUND_HALF_UP)
}

export const getCountryByCountryCode = (cc) =>
  R.find(R.propEq('value', cc))(issuerCountries)

export const capitalizeString = (string) => {
  string = string.toLowerCase()
  return string[0].toUpperCase() + string.substring(1)
}

export const capitalizeText = (text) => {
  return text.toLowerCase().charAt(0).toUpperCase() + text.slice(1)
}

export const formatDateWithHour = (date) => {
  const validatedDate = validateAndCastMomentDate(date, false)
  return validatedDate.format('hh:mmA DD MMM YYYY')
}

export const formatDate = (date, format = 'D MMM YYYY', utc = false) => {
  if (typeof format === 'boolean') {
    utc = format
    format = 'D MMM YYYY'
  }
  date = validateAndCastMomentDate(date, utc)
  return date.format(format)
}

export const dateToString = (date) => {
  // Format: dd(num) mm(str) yyyy(num)
  return date ? formatDate(date) : 'N.A.'
}

export const isDefined = R.pipe(R.isNil, R.not)

export const arrayContains = R.flip(R.contains)

export const currencyToSymbol = (currencyISO) => {
  return CURRENCY_ISO_CODE_SYMBOL_MAP[currencyISO]
}

export const dateToDisplay = (date) => {
  return moment(date).format('D/MM/YYYY')
}

export const removeFalsyProperties = (obj) => {
  const filterProperty = (value) =>
    !(
      value === null ||
      value === undefined ||
      value === '' ||
      value.length === 0
    )

  return R.filter(filterProperty, obj)
}
